import { ref, watch } from 'vue'

// Sons de l'interface, synthetises via Web Audio.
// Aucun fichier audio a heberger : rien a telecharger, et le poids dans le
// bundle est nul.

type SoundKey = 'message' | 'notification'

const STORAGE_KEY = 'agei.sound.enabled'

// Chaque son a son propre reglage, independamment des autres.
const enabled = ref<Record<SoundKey, boolean>>({ message: true, notification: true })

if (typeof localStorage !== 'undefined') {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (typeof parsed?.message === 'boolean') enabled.value.message = parsed.message
      if (typeof parsed?.notification === 'boolean') enabled.value.notification = parsed.notification
    }
  } catch {
    // preferences corrompues : on garde les valeurs par defaut
  }
}

watch(
  enabled,
  (value) => {
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    } catch {
      // mode prive ou quota plein : le son fonctionne quand meme
    }
  },
  { deep: true },
)

let ctx: AudioContext | null = null
let primed = false

// Les navigateurs bloquent la lecture audio tant que l'utilisateur n'a pas
// interagi avec la page. OnPrepare le contexte au premier clic ou frappe,
// sinon le premier son de la session serait muet.
function primeAudio() {
  if (primed) return
  try {
    ctx = ctx ?? new (window.AudioContext || (window as any).webkitAudioContext)()
    if (ctx.state === 'suspended') void ctx.resume()
    primed = true
  } catch {
    ctx = null
  }
}

if (typeof window !== 'undefined') {
  for (const event of ['pointerdown', 'keydown', 'touchstart'] as const) {
    window.addEventListener(event, primeAudio, { once: true, passive: true })
  }
}

// Une note : sinus avec attaque et extinction douce pour eviter le claquement.
function note(frequency: number, startAt: number, duration: number, gain: number) {
  if (!ctx) return
  const osc = ctx.createOscillator()
  const amp = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(frequency, startAt)
  amp.gain.setValueAtTime(0.0001, startAt)
  amp.gain.exponentialRampToValueAtTime(gain, startAt + 0.012)
  amp.gain.exponentialRampToValueAtTime(0.0001, startAt + duration)
  osc.connect(amp).connect(ctx.destination)
  osc.start(startAt)
  osc.stop(startAt + duration + 0.02)
}

function play(notes: Array<[number, number, number, number]>) {
  if (!ctx || ctx.state === 'suspended') {
    primeAudio()
    if (!ctx || ctx.state === 'suspended') return
  }
  const now = ctx.currentTime
  for (const [frequency, offset, duration, gain] of notes) {
    note(frequency, now + offset, duration, gain)
  }
}

// Son de message : deux notes aigues, bref et clair, facon messenger.
export function playMessageSound() {
  if (!enabled.value.message) return
  primeAudio()
  play([
    [1318.5, 0, 0.16, 0.16],
    [1760.0, 0.09, 0.2, 0.13],
  ])
}

// Son de notification : trois notes descendantes, plus grave et plus longues,
// pour etre distingue du message sans avoir a regarder l'ecran.
export function playNotificationSound() {
  if (!enabled.value.notification) return
  primeAudio()
  play([
    [1046.5, 0, 0.18, 0.13],
    [880.0, 0.11, 0.2, 0.12],
    [659.3, 0.22, 0.3, 0.1],
  ])
}

export function isSoundEnabled(key: SoundKey) {
  return enabled.value[key]
}

export function toggleSound(key: SoundKey) {
  enabled.value[key] = !enabled.value[key]
  // On reactive le contexte au clic : c'est aussi la premiere interaction.
  primeAudio()
  if (enabled.value[key]) (key === 'message' ? playMessageSound : playNotificationSound)()
}
